import React, { useState } from 'react';
import {
  MailOutlined,
  PhoneOutlined,
  WhatsAppOutlined,
  GlobalOutlined,
  PictureOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { Dropdown, Modal } from 'antd';
import { useTranslation } from 'react-i18next';

const initialsOf = (name) =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');

const firstWebsite = (website) => {
  const first = (website || '').split(',')[0].trim();
  if (!first) return '';
  return /^https?:\/\//i.test(first) ? first : `https://${first}`;
};

const ActionLink = ({ href, label, children, external }) => (
  <a
    href={href}
    aria-label={label}
    title={label}
    {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    className="w-9 h-9 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center hover:bg-blue-100 hover:text-blue-700 transition-colors"
  >
    {children}
  </a>
);

// One saved card as a compact row: avatar or photo, who they are, one-tap contact
// actions, and the full details behind "More".
const SavedCardItem = ({ card, onEdit, onDelete, onViewPhotos }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const hasPhotos = Boolean(card.frontImageUrl || card.backImageUrl);
  const phoneDigits = (card.phone || '').replace(/\D/g, '');
  const website = firstWebsite(card.website);
  const hasDetails = Boolean(card.email || card.phone || card.website || card.address || card.notes);

  const confirmDelete = () =>
    Modal.confirm({
      title: t('businessCardScanner.deleteTitle'),
      content: card.name,
      okText: t('businessCardScanner.delete'),
      okButtonProps: { danger: true },
      cancelText: t('businessCardScanner.cancel'),
      onOk: () => onDelete(card.id),
    });

  const menuItems = [
    { key: 'edit', icon: <EditOutlined />, label: t('businessCardScanner.edit'), onClick: () => onEdit(card) },
    { key: 'delete', icon: <DeleteOutlined />, label: t('businessCardScanner.delete'), danger: true, onClick: confirmDelete },
  ];

  return (
    <div className="border border-gray-200 rounded-xl bg-white p-3 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        {hasPhotos ? (
          <button
            type="button"
            onClick={() => onViewPhotos({ front: card.frontImageUrl, back: card.backImageUrl })}
            aria-label={t('businessCardScanner.viewPhotos')}
            title={t('businessCardScanner.viewPhotos')}
            className="relative shrink-0 w-[84px] aspect-[7/4] rounded-lg overflow-hidden bg-gray-900"
          >
            <img
              src={card.frontImageUrl || card.backImageUrl}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover"
            />
            {card.frontImageUrl && card.backImageUrl && (
              <span className="absolute bottom-0.5 end-0.5 px-1 rounded bg-black/65 text-white text-[10px] leading-4 flex items-center gap-0.5">
                <PictureOutlined />2
              </span>
            )}
          </button>
        ) : (
          <div
            aria-hidden="true"
            className="shrink-0 w-11 h-11 rounded-full bg-blue-100 text-blue-700 font-semibold flex items-center justify-center"
          >
            {initialsOf(card.name)}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-gray-900 truncate">{card.name}</h4>
          <p className="text-sm text-gray-600 truncate">{[card.position, card.company].filter(Boolean).join(' · ')}</p>
        </div>

        <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
          <button
            type="button"
            aria-label={t('businessCardScanner.more')}
            className="shrink-0 w-8 h-8 rounded-full text-gray-500 hover:bg-gray-100 flex items-center justify-center"
          >
            <MoreOutlined />
          </button>
        </Dropdown>
      </div>

      {(card.phone || card.email || website) && (
        <div className="flex gap-2 mt-3">
          {card.phone && (
            <ActionLink href={`tel:${card.phone.replace(/\s+/g, '')}`} label={t('businessCardScanner.call')}>
              <PhoneOutlined />
            </ActionLink>
          )}
          {phoneDigits.length >= 8 && (
            <ActionLink href={`https://wa.me/${phoneDigits}`} label="WhatsApp" external>
              <WhatsAppOutlined />
            </ActionLink>
          )}
          {card.email && (
            <ActionLink href={`mailto:${card.email}`} label={t('businessCardScanner.emailAction')}>
              <MailOutlined />
            </ActionLink>
          )}
          {website && (
            <ActionLink href={website} label={t('businessCardScanner.websiteAction')} external>
              <GlobalOutlined />
            </ActionLink>
          )}
        </div>
      )}

      {expanded && hasDetails && (
        <div className="mt-3 space-y-1 text-sm text-gray-600 break-words">
          {card.email && <p>{card.email}</p>}
          {card.phone && <p dir="ltr" className="text-start">{card.phone}</p>}
          {card.website && <p>{card.website}</p>}
          {card.address && <p>{card.address}</p>}
          {card.notes && <p className="whitespace-pre-wrap text-gray-700">{card.notes}</p>}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-2 text-xs text-gray-400">
        <div className="flex items-center gap-2 min-w-0">
          {card.tag && (
            <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-medium truncate">{card.tag}</span>
          )}
          <span className="shrink-0">
            {new Date(card.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        </div>
        {hasDetails && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="shrink-0 text-blue-600 font-medium flex items-center gap-1"
          >
            {expanded ? t('businessCardScanner.less') : t('businessCardScanner.moreDetails')}
            {expanded ? <UpOutlined /> : <DownOutlined />}
          </button>
        )}
      </div>
    </div>
  );
};

export default SavedCardItem;
